import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserSession, useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { useDismissedNotifications } from "shared/mobile/hooks/useDismissedNotifications";
import { useGroupActions } from "features/groups/hooks/useGroupActions";
import { useGroupDetails } from "features/groups/hooks/useGroupDetails";
import { useTransactionsLocal } from "features/groups/hooks/useTransactionsLocal";
import { useTranslation } from "shared/hooks/useTranslation";
import { toMLLanguage } from "constants/mlApi";
import {
    formatCurrencyAmountOrMasked,
    languageToLocale,
    normalizeTransactionParticipants,
} from "shared/money/balances";
import { calcUserDatedBalanceContributions } from "shared/money/currencyConversion";
import { buildGroupGuideItems } from "lib/guideBuilders";
import { displayCurrencyForBalance, MONEY_EPSILON, primaryCurrencyForTransactions } from "features/groups/lib/groupBalancesDisplay";
import type { BalanceSummary } from "features/groups/lib/groupDetailTypes";
import { activePeopleFor, createParticipantDisplayNameResolver, dedupePeopleById, participantDisplayName } from "features/groups/lib/groupPeople";
import type { AppNotification } from "types/Notification";
import type { GroupTransaction } from "types/GroupTransaction";
import type { UserEntry } from "types/Group";

export interface UseGroupDetailsScreenParams {
    groupName: string;
}

export interface GroupDetailsStickyHeader {
    name: string;
    balanceLabel: string;
    balanceColor: "success" | "error";
}

/** Group + transaction list shape used by TransactionList / Add flow. */
export interface GroupTransactionListModel {
    _id: string;
    name: string;
    people: UserEntry[];
    inviteToken: string;
    transactions: GroupTransaction[];
    userAliases: Record<string, string>;
}

export function useGroupDetailsScreen({ groupName }: UseGroupDetailsScreenParams) {
    const { profile } = useUserSession();
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible } = useFinancialVisibility();
    const { convertDatedTotal, convertBalances } = useCurrencyConversion();
    const { currentLanguage, t, getPathWithLanguage } = useTranslation();
    const navigate = useNavigate();
    const locale = languageToLocale(currentLanguage);
    const username = profile?.username ?? "";
    const historyRef = useRef<HTMLDivElement | null>(null);
    const settingsRef = useRef<HTMLDivElement | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [addExpenseOpen, setAddExpenseOpen] = useState(false);
    const notificationStorageKey = `group-notifications:${profile?.userId ?? username}`;
    const { dismissNotification, filterDismissedNotifications } = useDismissedNotifications(
        notificationStorageKey,
        profile?.token,
    );

    const {
        selectedGroup,
        setSelectedGroup,
        transactions,
        expenses,
        settlements,
        individualBalances,
        userAliases,
        individualBalancesByCurrency,
        userBalanceByCurrency,
        pairwiseDebts,
        currentParticipantId,
        loading,
        loadError,
        refetch,
    } = useGroupDetails({ username, userId: profile?.userId, groupName });

    const allPeople = useMemo(() => dedupePeopleById(selectedGroup?.people ?? []), [selectedGroup?.people]);
    const people = useMemo(() => activePeopleFor(allPeople), [allPeople]);
    const displayGroup = useMemo(() => ({ ...selectedGroup, people: allPeople }), [selectedGroup, allPeople]);
    const personNameFor = useMemo(
        () => createParticipantDisplayNameResolver(allPeople, t("common.unknownParticipant")),
        [allPeople, t],
    );

    const onTransactionsChange = useCallback(
        (next: GroupTransaction[]) => {
            if (selectedGroup) setSelectedGroup({ ...selectedGroup, transactions: next });
        },
        [selectedGroup, setSelectedGroup],
    );

    const transactionsGroup = useMemo(
        () => ({
            _id: selectedGroup?._id ?? "",
            name: selectedGroup?.name ?? "",
            people: allPeople,
            inviteToken: selectedGroup?.inviteToken ?? "",
            transactions: transactions as GroupTransaction[],
            userAliases: {} as Record<string, string>,
        }),
        [selectedGroup, allPeople, transactions],
    );

    const { handleAddTransaction } = useTransactionsLocal(
        transactionsGroup,
        transactions as GroupTransaction[],
        toMLLanguage(currentLanguage),
        onTransactionsChange,
    ) as { handleAddTransaction: (transaction: GroupTransaction) => void };

    const transactionListGroup: GroupTransactionListModel = useMemo(
        () => ({
            _id: selectedGroup?._id ?? "",
            name: selectedGroup?.name ?? "",
            people: allPeople,
            inviteToken: selectedGroup?.inviteToken ?? "",
            transactions: transactions as GroupTransaction[],
            userAliases,
        }),
        [selectedGroup, allPeople, transactions, userAliases],
    );

    const participantLabels = useMemo(
        () => Object.fromEntries(allPeople.map((person) => [person.id, participantDisplayName(person)])),
        [allPeople],
    );
    const groupPrimaryCurrency = useMemo(
        () => primaryCurrencyForTransactions(expenses as GroupTransaction[], userSettings.displayCurrency),
        [expenses, userSettings.displayCurrency],
    );
    const balanceDisplayCurrency = useMemo(
        () => displayCurrencyForBalance(userBalanceByCurrency, groupPrimaryCurrency, userSettings.displayCurrency),
        [groupPrimaryCurrency, userBalanceByCurrency, userSettings.displayCurrency],
    );

    const groupActions = useGroupActions({
        selectedGroup,
        setSelectedGroup,
        currentLanguage: toMLLanguage(currentLanguage),
    });

    const balanceSummary = useMemo<BalanceSummary>(() => {
        const byCurrency = userBalanceByCurrency ?? {};
        const currencyKeys = Object.keys(byCurrency);
        const normalizedTransactions = normalizeTransactionParticipants(
            transactions as GroupTransaction[],
            allPeople,
        );
        const amount = currencyKeys.length <= 1
            ? convertBalances(byCurrency, { toCurrency: balanceDisplayCurrency })
            : convertDatedTotal(
                calcUserDatedBalanceContributions(normalizedTransactions, currentParticipantId ?? ""),
                { toCurrency: balanceDisplayCurrency },
            );
        const amountLabel = formatCurrencyAmountOrMasked(Math.abs(amount), balanceDisplayCurrency, balancesVisible, locale);

        if (amount > MONEY_EPSILON) {
            return {
                amount,
                amountLabel,
                currency: balanceDisplayCurrency,
                tone: "positive",
                statusLabel: t("balances.youAreOwed"),
                description: t("groupDetail.owedDescription"),
                supportText: t("groupDetail.owedSupport"),
            };
        }
        if (amount < -MONEY_EPSILON) {
            return {
                amount,
                amountLabel,
                currency: balanceDisplayCurrency,
                tone: "negative",
                statusLabel: t("balances.youOwe"),
                description: t("groupDetail.debtDescription"),
                supportText: t("groupDetail.debtSupport"),
            };
        }
        return {
            amount: 0,
            amountLabel,
            currency: balanceDisplayCurrency,
            tone: "settled",
            statusLabel: t("balances.allSettled"),
            description: t("groupDetail.settledDescription"),
            supportText: t("groupDetail.settledSupport"),
        };
    }, [
        allPeople,
        balanceDisplayCurrency,
        balancesVisible,
        convertBalances,
        convertDatedTotal,
        currentParticipantId,
        locale,
        t,
        transactions,
        userBalanceByCurrency,
    ]);

    const recentMovements = useMemo(() => (expenses as GroupTransaction[]).slice(0, 4), [expenses]);

    const rawGroupNotifications = useMemo(
        () => selectedGroup?._id
            ? buildGroupGuideItems({
                groups: [selectedGroup],
                username,
                userId: profile?.userId,
                balancesVisible,
                locale,
                language: currentLanguage,
                max: 12,
                groupId: selectedGroup._id,
            })
            : [],
        [balancesVisible, locale, profile?.userId, selectedGroup, username, currentLanguage],
    );
    const groupNotifications: AppNotification[] = useMemo(
        () => filterDismissedNotifications(rawGroupNotifications).slice(0, 2),
        [filterDismissedNotifications, rawGroupNotifications],
    );

    const openHistory = useCallback(() => {
        setHistoryOpen(true);
        window.requestAnimationFrame(() => {
            historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, []);

    const openSettings = useCallback(() => {
        settingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const handleBack = useCallback(() => {
        navigate(getPathWithLanguage(`/${profile.username}/groups`));
    }, [getPathWithLanguage, navigate, profile.username]);

    const stickyHeader = useMemo((): GroupDetailsStickyHeader | null => {
        if (!selectedGroup?.name) return null;
        const formatted = formatCurrencyAmountOrMasked(
            Math.abs(balanceSummary.amount),
            balanceSummary.currency,
            balancesVisible,
            locale,
        );
        return {
            name: selectedGroup.name,
            balanceLabel:
                balanceSummary.tone === "settled"
                    ? t("balances.allSettled")
                    : `${balanceSummary.statusLabel} ${formatted} ${balanceSummary.currency}`,
            balanceColor: balanceSummary.tone === "negative" ? "error" : "success",
        };
    }, [balanceSummary, balancesVisible, locale, selectedGroup?.name, t]);

    return {
        t,
        locale,
        balancesVisible,
        historyRef,
        settingsRef,
        historyOpen,
        setHistoryOpen,
        addExpenseOpen,
        setAddExpenseOpen,
        data: {
            selectedGroup,
            loading,
            loadError,
            refetch,
            transactions,
            expenses,
            settlements,
            individualBalances,
            userAliases,
            individualBalancesByCurrency,
            userBalanceByCurrency,
            pairwiseDebts,
            currentParticipantId,
        },
        people: {
            allPeople,
            people,
            displayGroup,
            personNameFor,
        },
        transactionsView: {
            transactionListGroup,
            onTransactionsChange,
            handleAddTransaction,
            participantLabels,
        },
        movements: {
            recentMovements,
        },
        balances: {
            balanceSummary,
            balanceDisplayCurrency,
            groupPrimaryCurrency,
        },
        notifications: {
            groupNotifications,
            dismissNotification,
        },
        actions: {
            handleUpdateGroup: groupActions.handleUpdateGroup,
            handleRemoveMember: groupActions.handleRemoveMember,
            handleDeleteGroup: groupActions.handleDeleteGroup,
            handleSettleDebt: groupActions.handleSettleDebt,
            handleConfirmSettlement: groupActions.handleConfirmSettlement,
            handleRejectSettlement: groupActions.handleRejectSettlement,
            settlingDebtKey: groupActions.settlingDebtKey,
            confirmingSettlementId: groupActions.confirmingSettlementId,
            rejectingSettlementId: groupActions.rejectingSettlementId,
        },
        navigation: {
            openHistory,
            openSettings,
            handleBack,
        },
        stickyHeader,
    };
}
